import { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { t } from "i18next";
import { useAppSelector } from "@hooks/redux";
import { selectTiktokLinkedAccounts } from "@store/linkedAccountSlice";
import { useTikTokAccounts } from "@pages/me/hooks/useTikTokAccounts";
import { ConnectTiktokAccount } from "./ConnectTiktokAccount";
import { TikTokProductForm } from "./TikTokProductForm";
import { PostToTikTokEditorLayout } from "./PostToTikTokEditorLayout";
import { ClipLoading } from "@src/ui/Loading";
import { ModalHeader } from "./ModalHeader";
import { DelayedClipLoading } from "@src/ui/Loading/DelayedClipLoading";
import { DangerConfirmModal } from "@components/DangerConfirmModal";

export interface PostToTikTokModalProps {
  open: boolean;
  assetId: number;
  preferredLinkId?: number;
  onClose: () => void;
}

export const PostToTikTokModal = ({ open, onClose, assetId, preferredLinkId }: PostToTikTokModalProps) => {
  const accounts = useAppSelector(selectTiktokLinkedAccounts);
  const hasAccount = accounts.length > 0;
  const initialLinkId = preferredLinkId ?? accounts[0]?.id;
  const getTikTokAccounts = useTikTokAccounts();
  const accountsLoading = getTikTokAccounts.isPending();
  const modalWidth = hasAccount || accountsLoading ? 993 : 720;
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      void getTikTokAccounts();
    } else {
      setDirty(false);
      setConfirmOpen(false);
    }
  }, [open, getTikTokAccounts]);

  const handleDirtyChange = useCallback((next: boolean) => {
    setDirty(next);
  }, []);

  const handleCancel = () => {
    if (dirty) {
      setConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleLeave = () => {
    setConfirmOpen(false);
    onClose();
  };

  const handleStay = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={modalWidth}
        centered
        destroyOnHidden
        mask={{ closable: false }}
        closable={false}
        styles={{ container: { padding: 0 }, body: { padding: 0 } }}
      >
        <DelayedClipLoading
          loading={getTikTokAccounts.isPending()}
          LoadingElement={() => (
            <PostToTikTokEditorLayout videoLoading>
              <ModalHeader onClose={handleCancel} />
              <ClipLoading className="min-h-[664px]" />
            </PostToTikTokEditorLayout>
          )}
        >
          {hasAccount ? (
            <TikTokProductForm
              open={open}
              onClose={handleCancel}
              onDirtyChange={handleDirtyChange}
              accounts={accounts}
              initialLinkId={initialLinkId}
              assetId={assetId}
            />
          ) : (
            <ConnectTiktokAccount onClose={handleCancel} />
          )}
        </DelayedClipLoading>
      </Modal>
      <DangerConfirmModal
        open={confirmOpen}
        title={t("Leave Post Editor?")}
        description={t("All current edits will be lost and cannot be recovered.")}
        confirmText={t("Leave")}
        onConfirm={handleLeave}
        onCancel={handleStay}
      />
    </>
  );
};
